import { useRef, useState } from "react";
import { useForm, router } from "@inertiajs/react";
import AppShell from "../../components/Shell/AppShell";
import { common, pick } from "../../lib/i18n";
import type { BrandDetail, CurrentUser, ProductSummary } from "../../types";

const copy = {
  pl: {
    maxLogos: (r: number, m: number) => `Możesz dodać maksymalnie ${r} logo (limit: ${m})`,
    invalidFormat: (n: string) => `Niedozwolony format: ${n}. Akceptowane: PNG, WebP, JPG.`,
    tooLarge: (n: string) => `Plik ${n} przekracza limit 5 MB.`,
    confirmDeleteLogo: "Usunąć to logo?", newBrand: "Nowa marka", editBrand: (n?: string) => `Edytuj: ${n}`,
    basicInfo: "Podstawowe informacje", brandName: "Nazwa marki", brandNamePlaceholder: "np. AvantoAI Studio",
    descriptionOpt: "Opis (opcjonalny)", descPlaceholder: "Krótki opis marki widoczny w panelu.",
    promptHintsOpt: "Wskazówki do promptów (opcjonalne)", promptHintsPlaceholder: "np. Styl skandynawski, minimalizm, paleta biel + drewno...",
    promptHintsNote: "Trafia bezpośrednio do promptu generacji jako art direction.", activeLabel: "Aktywna (widoczna w formularzu generacji)",
    createBrand: "Utwórz markę", saveChanges: "Zapisz zmiany", deleteLogo: "Usuń logo", uploading: "Wgrywanie…", addLogo: "+ Dodaj logo",
    logoHint: (m: number) => `PNG, WebP lub JPG · max 5 MB · max ${m} plików`,
    logoLimitReached: (m: number) => `Osiągnięto limit ${m} logo. Usuń istniejące, żeby dodać nowe.`,
  },
  en: {
    maxLogos: (r: number, m: number) => `You can add at most ${r} logos (limit: ${m})`,
    invalidFormat: (n: string) => `Unsupported format: ${n}. Accepted: PNG, WebP, JPG.`,
    tooLarge: (n: string) => `File ${n} exceeds the 5 MB limit.`,
    confirmDeleteLogo: "Delete this logo?", newBrand: "New brand", editBrand: (n?: string) => `Edit: ${n}`,
    basicInfo: "Basic information", brandName: "Brand name", brandNamePlaceholder: "e.g. AvantoAI Studio",
    descriptionOpt: "Description (optional)", descPlaceholder: "A short brand description shown in the panel.",
    promptHintsOpt: "Prompt hints (optional)", promptHintsPlaceholder: "e.g. Scandinavian style, minimalism, white + wood palette...",
    promptHintsNote: "Goes directly into the generation prompt as art direction.", activeLabel: "Active (visible in the generation form)",
    createBrand: "Create brand", saveChanges: "Save changes", deleteLogo: "Delete logo", uploading: "Uploading…", addLogo: "+ Add logo",
    logoHint: (m: number) => `PNG, WebP or JPG · max 5 MB · max ${m} files`,
    logoLimitReached: (m: number) => `Reached the limit of ${m} logos. Delete existing ones to add new.`,
  },
};

const MAX_LOGOS          = 5;
const MAX_LOGO_SIZE      = 5 * 1024 * 1024;
const ALLOWED_LOGO_TYPES = ["image/png", "image/webp", "image/jpeg"];

type Props = {
  brand: Partial<BrandDetail> & { id?: number; errors?: Record<string, string[]> };
  action: "create" | "update";
  current_user: CurrentUser;
  products?: ProductSummary[];
};

export default function BrandsForm({ brand, action, current_user, products = [] }: Props) {
  const initials = current_user.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const isNew    = action === "create";
  const t = pick(copy);
  const c = common();

  const { data, setData, post, patch, processing, errors } = useForm({
    name:         brand.name         ?? "",
    description:  brand.description  ?? "",
    prompt_hints: brand.prompt_hints ?? "",
    active:       brand.active       ?? true,
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isNew) {
      post("/brands");
    } else {
      patch(`/brands/${brand.id}`);
    }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [uploadingLogos, setUploadingLogos] = useState(false);

  const existingCount = brand.logo_urls?.length ?? 0;

  const handleLogoFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setLogoError(null);

    const selected = Array.from(files);
    const remaining = MAX_LOGOS - existingCount;

    if (selected.length > remaining) {
      setLogoError(t.maxLogos(remaining, MAX_LOGOS));
      return;
    }
    for (const file of selected) {
      if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
        setLogoError(t.invalidFormat(file.name));
        return;
      }
      if (file.size > MAX_LOGO_SIZE) {
        setLogoError(t.tooLarge(file.name));
        return;
      }
    }

    const form = new FormData();
    selected.forEach((f) => form.append("logos[]", f));
    setUploadingLogos(true);
    router.post(`/brands/${brand.id}/logos`, form, {
      onFinish: () => setUploadingLogos(false),
    });
  };

  const handleDestroyLogo = (logoId: number) => {
    if (!confirm(t.confirmDeleteLogo)) return;
    router.delete(`/brands/${brand.id}/logos/${logoId}`);
  };

  return (
    <AppShell products={products} user={{ name: current_user.name, initials, role: current_user.role }}>
      <div className="page" style={{ maxWidth: 640 }}>
        <div className="page-hd">
          <div>
            <h2>{isNew ? t.newBrand : t.editBrand(brand.name)}</h2>
          </div>
        </div>

        <form onSubmit={submit}>
          <div className="panel" style={{ marginBottom: 14 }}>
            <div className="panel-hd"><h3>{t.basicInfo}</h3></div>
            <div className="panel-body">
              <div className="field">
                <label>{t.brandName}</label>
                <input
                  className="input"
                  type="text"
                  value={data.name}
                  onChange={(e) => setData("name", e.target.value)}
                  placeholder={t.brandNamePlaceholder}
                />
                {errors.name && <span style={{ fontSize: 11.5, color: "var(--error, #c0392b)" }}>{errors.name}</span>}
              </div>

              <div className="field">
                <label>{t.descriptionOpt}</label>
                <textarea
                  className="input"
                  rows={4}
                  style={{ height: "auto", padding: "8px 10px", resize: "vertical" }}
                  value={data.description}
                  onChange={(e) => setData("description", e.target.value)}
                  placeholder={t.descPlaceholder}
                />
                {errors.description && <span style={{ fontSize: 11.5, color: "var(--error, #c0392b)" }}>{errors.description}</span>}
              </div>

              <div className="field">
                <label>{t.promptHintsOpt}</label>
                <textarea
                  className="input"
                  rows={3}
                  style={{ height: "auto", padding: "8px 10px", resize: "vertical" }}
                  value={data.prompt_hints}
                  onChange={(e) => setData("prompt_hints", e.target.value)}
                  placeholder={t.promptHintsPlaceholder}
                />
                <span style={{ fontSize: 11, color: "var(--muted-2)", marginTop: 2 }}>
                  {t.promptHintsNote}
                </span>
                {errors.prompt_hints && <span style={{ fontSize: 11.5, color: "var(--error, #c0392b)" }}>{errors.prompt_hints}</span>}
              </div>

              <div className="field">
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={data.active}
                    onChange={(e) => setData("active", e.target.checked)}
                  />
                  {t.activeLabel}
                </label>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginBottom: 24 }}>
            <button type="button" className="btn btn-ghost" onClick={() => history.back()}>
              {c.actions.cancel}
            </button>
            <button type="submit" className="btn btn-primary" disabled={processing}>
              {isNew ? t.createBrand : t.saveChanges}
            </button>
          </div>
        </form>

        {!isNew && (
          <div className="panel">
            <div className="panel-hd">
              <h3>Logo</h3>
              <span className="mono sub">{existingCount} / {MAX_LOGOS}</span>
            </div>
            <div className="panel-body">
              {brand.logo_urls && brand.logo_urls.length > 0 && (
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
                  {brand.logo_urls.map((logo) => (
                    <div
                      key={logo.id}
                      style={{
                        position: "relative",
                        background: "var(--bg-2)",
                        border: "1px solid var(--line)",
                        borderRadius: 6,
                        padding: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 100,
                        height: 80,
                      }}
                    >
                      <img
                        src={logo.url}
                        alt="logo"
                        style={{ maxHeight: 56, maxWidth: 80, objectFit: "contain" }}
                      />
                      <button
                        type="button"
                        onClick={() => handleDestroyLogo(logo.id)}
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          width: 18,
                          height: 18,
                          border: "none",
                          borderRadius: 3,
                          background: "var(--bg-3)",
                          color: "var(--muted)",
                          cursor: "pointer",
                          fontSize: 11,
                          lineHeight: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: 0,
                        }}
                        aria-label={t.deleteLogo}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {existingCount < MAX_LOGOS ? (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/webp,image/jpeg"
                    multiple
                    style={{ display: "none" }}
                    onChange={(e) => handleLogoFiles(e.target.files)}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogos}
                  >
                    {uploadingLogos ? t.uploading : t.addLogo}
                  </button>
                  <div style={{ fontSize: 11, color: "var(--muted-2)", marginTop: 6 }}>
                    {t.logoHint(MAX_LOGOS)}
                  </div>
                  {logoError && (
                    <div style={{ fontSize: 11.5, color: "var(--error, #c0392b)", marginTop: 6 }}>
                      {logoError}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 12, color: "var(--muted)" }}>
                  {t.logoLimitReached(MAX_LOGOS)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
